/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Grid } from '@mui/system';
import { isEmpty } from 'lodash';
import { CompanyMeasurementDate } from 'api';
import { DELETE_MEASUREMENT_DATE, DELETE_MEASUREMENT_DATE_CONFIRMATION } from 'common/constants/messages/validations';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { ConfirmationDelete, ConfirmationDialog, Dialog } from 'components/Dialogs';
import { DialogContext } from 'context';
import {
  useDeleteMeasurementDate,
  useGetAllMeasurementDatesByFirmId,
  useGetCompaniesAndFundByMd,
} from 'services/hooks/firm';
import DateSelector from './DateSelector/DateSelector';
import { ActionButtonsGrid, DangerButton, MarginTopGrid } from './styles';
import {
  DeleteMeasurementDateDialogProps,
  FundAndCompanyMeasurementDatesToDelete,
  SelectedItem,
  SelectedItemId,
} from './types';
import CompanyList from '../MeasurementDates/components/company-list/CompanyList';
import FundList from '../MeasurementDates/components/fund-list/FundList';
import { StyledDialog } from '../MeasurementDates/measurement-date-dialog/MeasurementDateDialog';

// @ts-ignore
const WrapperComponent = props => <ConfirmationDialog {...props} title="Delete" />;
// @ts-ignore
const ContentComponent = props => <ConfirmationDelete {...props} />;

const DeleteMeasurementDateDialog = ({
  open,
  onClose,
  getMeasurementDates,
  setMdHasBeenDeleted,
  selectedCmdId,
  selectedFmdId,
}: DeleteMeasurementDateDialogProps) => {
  const [storeValue] = useStore() as unknown as UseStoreValues;
  const { firmInfo } = storeValue;
  const [measurementDates, fetchMeasurementDates] = useGetAllMeasurementDatesByFirmId();
  const [companiesAndFunds, fetchCompaniesAndFunds] = useGetCompaniesAndFundByMd();
  const [deleteMeasurementDate] = useDeleteMeasurementDate();
  const [fundList, setFundList] = useState([]);
  const [selectedFunds, setSelectedFunds] = useState<SelectedItem>({});
  const [companyList, setCompanyList] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState<SelectedItem>({});
  const [isMdSelected, setIsMdSelected] = useState(false);
  const { showDialog } = useContext(DialogContext);

  const firmId = useMemo(() => firmInfo?.id ?? null, [firmInfo]);

  useEffect(() => {
    if (firmId && fetchMeasurementDates) {
      fetchMeasurementDates(firmId);
    }
  }, [firmId, fetchMeasurementDates]);

  const handleOnSelectMD = useCallback(
    md => {
      if (md && firmId) {
        setIsMdSelected(true);
        fetchCompaniesAndFunds(firmId, md);
      }
    },
    [fetchCompaniesAndFunds, firmId]
  );

  const getListOfId = useCallback(
    (data: SelectedItem): SelectedItemId[] =>
      Object.keys(data)
        .filter(key => data[parseInt(key, 10)] === true)
        .map(key => ({ id: parseInt(key, 10) })),
    []
  );

  const parsedSelectedItems = useMemo(
    () =>
      ({
        company_measurement_dates: getListOfId(selectedCompanies),
        fund_measurement_dates: getListOfId(selectedFunds),
      }) as FundAndCompanyMeasurementDatesToDelete,
    [selectedFunds, selectedCompanies, getListOfId]
  );

  const selectedItemsMsg = useMemo(() => {
    const data = parsedSelectedItems || {};
    const companiesLength = data?.company_measurement_dates?.length || 0;
    const fundsLength = data?.fund_measurement_dates?.length || 0;

    const hasCompanies = companiesLength > 0;
    const hasFunds = fundsLength > 0;

    let companyTxt = '';
    if (hasCompanies) {
      companyTxt = companiesLength > 1 ? 'Companies' : 'Company';
    }

    let fundTxt = '';
    if (hasFunds) {
      fundTxt = fundsLength > 1 ? 'Funds' : 'Fund';
    }

    const hasCompaniesAndFundsSelected = hasCompanies && hasFunds;

    const selectedItemText = `selected ${fundTxt}${hasCompaniesAndFundsSelected ? ' and ' : ''}${companyTxt}`;
    return selectedItemText;
  }, [parsedSelectedItems]);

  const onDeleteMd = useCallback(async () => {
    const data = parsedSelectedItems;
    const mdInData =
      !!data.company_measurement_dates.find(({ id }) => id === selectedCmdId) ||
      !!data.fund_measurement_dates.find(({ id }) => id.toString() === selectedFmdId?.toString());
    const result = await deleteMeasurementDate(data);

    if (result && getMeasurementDates) {
      setMdHasBeenDeleted(mdInData);
      // we pass in an empty object, as that will cause the page to be set to not being initialized anymore
      // this could use some more work so the underlying function makes an attempt to move to the next appropriate measurement date
      await getMeasurementDates({} as CompanyMeasurementDate);
    }
  }, [
    deleteMeasurementDate,
    getMeasurementDates,
    parsedSelectedItems,
    setMdHasBeenDeleted,
    selectedCmdId,
    selectedFmdId,
  ]);

  const handleDelete = useCallback(() => {
    const confirmationMsgTxt = DELETE_MEASUREMENT_DATE_CONFIRMATION(selectedItemsMsg);
    onClose();
    // @ts-ignore
    showDialog({
      wrapper: WrapperComponent,
      content: ContentComponent,
      contentProps: {
        customMessage: confirmationMsgTxt,
      },
      actions: [
        {
          label: 'Cancel',
          type: 'cancel',
        },
        {
          label: 'Delete',
          variant: 'contained',
          type: 'danger',
          callback: () => onDeleteMd(),
        },
      ],
    });
  }, [onClose, onDeleteMd, selectedItemsMsg, showDialog]);

  const allowDeleteMd = useMemo(() => isMdSelected && !isEmpty(selectedCompanies), [isMdSelected, selectedCompanies]);

  useEffect(() => {
    if (companiesAndFunds) {
      setFundList(companiesAndFunds.fund_measurement_dates ?? []);
      setCompanyList(companiesAndFunds.company_measurement_dates ?? []);
    }
  }, [companiesAndFunds]);

  return (
    <StyledDialog
      open={open}
      maxWidth="lg"
      title={DELETE_MEASUREMENT_DATE}
      onClose={onClose}
      fullWidth
      dialogProps={{
        disableBackdropClick: true,
        disableEscapeKeyDown: true,
        disableTitleClose: false,
      }}>
      <Grid size={{ sm: 6 }}>
        <Grid>
          <DateSelector options={measurementDates} onSelectMD={handleOnSelectMD} />
        </Grid>
      </Grid>
      <MarginTopGrid container spacing={4}>
        <Grid size={{ sm: 6 }}>
          <FundList
            fundList={fundList}
            selectedFunds={selectedFunds}
            setSelectedFunds={setSelectedFunds}
            selectedCompanies={selectedCompanies}
            setSelectedCompanies={setSelectedCompanies}
            isLoading={false}
            isDeletingMD
          />
        </Grid>
        <Grid size={{ sm: 6 }}>
          <CompanyList
            companyList={companyList}
            selectedCompanies={selectedCompanies}
            setSelectedCompanies={setSelectedCompanies}
            isLoading={false}
            isDeletingMD
          />
        </Grid>
      </MarginTopGrid>
      <ActionButtonsGrid container justifyContent="flex-end" spacing={2}>
        <Grid>
          <DangerButton
            id="delete-md-btn"
            variant="contained"
            color="secondary"
            disabled={!allowDeleteMd}
            onClick={handleDelete}>
            DELETE
          </DangerButton>
        </Grid>
      </ActionButtonsGrid>
    </StyledDialog>
  );
};

export default DeleteMeasurementDateDialog;
